/** @jsxImportSource @emotion/react */
import React from 'react'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import Typography from '@mui/material/Typography'
import { toast } from 'utils/toast'
import { PageLoader } from 'components/PageLoader'
import { FIND_ONE_AI_FLT, UPDATE_AI_FLT } from 'queries/responseTests'
import { findOneAiFullLengthTest } from '__generatedTypes__/findOneAiFullLengthTest'
import { updateAiFullLengthTest, updateAiFullLengthTestVariables } from '__generatedTypes__/updateAiFullLengthTest'
import { AITestResponseTypeEnum } from '__generatedTypes__/globalTypes'
import * as responseTestsClasses from 'pagesNew/ResponseTests/styles'
import { ResponseTestsSection } from './ResponseTestsSection'
import { Form, FormValues } from './Form'

export const Dashboard = () => {
  const apolloClient = useApolloClient()
  const [updateFullLengthTest] = useMutation<updateAiFullLengthTest, updateAiFullLengthTestVariables>(UPDATE_AI_FLT)
  const { data, loading } = useQuery<findOneAiFullLengthTest>(FIND_ONE_AI_FLT, {
    fetchPolicy: 'cache-and-network',
  })
  const fullLengthTest = data?.findOneCasperFullLengthTest

  const handleSubmit = async (values: FormValues) => {
    if (!fullLengthTest) {
      return
    }

    try {
      await updateFullLengthTest({
        variables: {
          input: {
            id: fullLengthTest.id,
            title: values.title,
            description: values.description,
            membershipLevel: values.membershipLevel,
          },
        },
      })

      apolloClient.cache.modify({
        id: apolloClient.cache.identify({
          __typename: fullLengthTest.__typename,
          id: fullLengthTest.id,
        }),
        fields: {
          title: () => values.title,
          description: () => values.description,
          membershipLevel: () => values.membershipLevel,
        },
      })

      toast.success('Full-Length Test saved successfully')
    } catch (error) {
      toast.error('Error while saving Full-Length Test')
    }
  }

  if (loading || !fullLengthTest) {
    return <PageLoader />
  }

  return (
    <section css={responseTestsClasses.container}>
      <Typography css={responseTestsClasses.header} variant="title3">
        Full-Length Test
      </Typography>

      <Form test={fullLengthTest} onSubmit={handleSubmit} />

      <Typography css={responseTestsClasses.header} variant="title4">
        Video-Response
      </Typography>
      <ResponseTestsSection
        fltId={fullLengthTest.id}
        allTests={fullLengthTest.aiTests}
        responseType={AITestResponseTypeEnum.VideoResponse}
      />

      <Typography css={responseTestsClasses.header} variant="title4">
        Typed-Response
      </Typography>
      <ResponseTestsSection
        fltId={fullLengthTest.id}
        allTests={fullLengthTest.aiTests}
        responseType={AITestResponseTypeEnum.TextResponse}
      />
    </section>
  )
}
